import { DurationPicker, TimePickerInput } from '@/components'
import TextField from '@/components/skeletons/TextField'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatDateWithFormatter } from '@/utils/function'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getRoomAvailable } from '../../services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface TimeRange {
    start: string // Format: "HH:mm"
    end: string // Format: "HH:mm"
}

interface AvailableTimePickerProps {
    selectedRoom?: string
    selectedDate: string
    startTime?: string
    duration?: number
    onStartTimeChange: (time: string) => void
    onDurationChange: (duration: number) => void
    startTimeLabel?: string
    durationLabel?: string
    className?: string
    startTimeError?: string
    durationError?: string
    setStartTimeError?: (error?: string) => void
    setDurationError?: (error?: string) => void
}

// Helper function to convert time string to minutes
const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number)
    return hours * 60 + minutes
}

// Check if two time ranges overlap
const isTimeRangeOverlap = (
    start1: number,
    end1: number,
    start2: number,
    end2: number
): boolean => {
    return start1 < end2 && end1 > start2
}

const AvailableTimePicker: React.FC<AvailableTimePickerProps> = ({
    selectedRoom,
    selectedDate,
    startTime,
    duration = 30,
    onStartTimeChange,
    onDurationChange,
    startTimeLabel,
    durationLabel,
    className,
    startTimeError,
    durationError,
    setStartTimeError,
    setDurationError,
}) => {
    const { t } = useTranslation()

    const { data: roomAvailable, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EVENTS_ROOM_AVAILABLE, selectedDate],
        queryFn: () =>
            getRoomAvailable(
                formatDateWithFormatter(selectedDate, 'YYYY-MM-DD')
            ),
        enabled: !!selectedDate,
        loadingComponent: <TextField />,
    })

    // Filter bookings for selected room and date (including multi-day bookings)
    const roomBookings = useMemo(() => {
        if (!selectedRoom || !selectedDate) return []

        console.log('roomAvailable', roomAvailable, selectedRoom)

        const selectedDateFormatted = formatDateWithFormatter(
            selectedDate,
            'YYYY-MM-DD'
        )

        return roomAvailable?.results?.flat().filter((booking) => {
            if (booking.room_name !== selectedRoom) return false

            const bookingStartDate = formatDateWithFormatter(
                booking.start_date,
                'YYYY-MM-DD'
            )

            // Calculate booking end date and time
            const startDateTime = new Date(
                `${bookingStartDate}T${booking.start_time}`
            )
            const endDateTime = new Date(
                startDateTime.getTime() + booking.duration_minutes * 60000
            )
            const bookingEndDate = formatDateWithFormatter(
                endDateTime.toISOString(),
                'YYYY-MM-DD'
            )

            // Check if the selected date falls within the booking period
            // Booking affects selected date if:
            // 1. Booking starts on selected date, OR
            // 2. Booking ends on selected date, OR
            // 3. Selected date is between booking start and end dates
            return (
                bookingStartDate === selectedDateFormatted ||
                bookingEndDate === selectedDateFormatted ||
                (bookingStartDate < selectedDateFormatted &&
                    bookingEndDate > selectedDateFormatted)
            )
        })
    }, [selectedRoom, selectedDate, roomAvailable])

    // Calculate disabled time ranges
    const disabledTimeRanges = useMemo((): TimeRange[] => {
        if (!selectedRoom || !selectedDate || roomBookings?.length === 0) {
            return []
        }

        const selectedDateFormatted = formatDateWithFormatter(
            selectedDate,
            'YYYY-MM-DD'
        )

        return roomBookings
            ? roomBookings?.map((booking) => {
                  const bookingStartDate = formatDateWithFormatter(
                      booking.start_date,
                      'YYYY-MM-DD'
                  )

                  // Calculate booking end date and time
                  const startDateTime = new Date(
                      `${bookingStartDate}T${booking.start_time}`
                  )
                  const endDateTime = new Date(
                      startDateTime.getTime() + booking.duration_minutes * 60000
                  )
                  const bookingEndDate = formatDateWithFormatter(
                      endDateTime.toISOString(),
                      'YYYY-MM-DD'
                  )
                  const endTime = endDateTime.toTimeString().slice(0, 5) // HH:mm format

                  // Determine the actual time range that affects the selected date
                  let startTime: string
                  let endTimeForDay: string

                  if (bookingStartDate === selectedDateFormatted) {
                      // Booking starts on selected date
                      startTime = booking.start_time
                      if (bookingEndDate === selectedDateFormatted) {
                          // Booking also ends on selected date
                          endTimeForDay = endTime
                      } else {
                          // Booking extends beyond selected date
                          endTimeForDay = '23:59'
                      }
                  } else {
                      // Booking started before selected date
                      startTime = '00:00'
                      if (bookingEndDate === selectedDateFormatted) {
                          // Booking ends on selected date
                          endTimeForDay = endTime
                      } else {
                          // Booking spans entire selected date
                          endTimeForDay = '23:59'
                      }
                  }

                  return {
                      start: startTime,
                      end: endTimeForDay,
                  }
              })
            : []
    }, [selectedRoom, selectedDate, roomBookings])

    const handleStartTimeChange = (time: string) => {
        // Check if the new time would conflict with existing bookings
        const proposedStart = timeToMinutes(time)
        const proposedEnd = proposedStart + duration

        const wouldConflict = disabledTimeRanges?.some((range) => {
            const rangeStart = timeToMinutes(range.start)
            const rangeEnd = timeToMinutes(range.end)
            return isTimeRangeOverlap(
                proposedStart,
                proposedEnd,
                rangeStart,
                rangeEnd
            )
        })

        if (wouldConflict && selectedRoom) {
            // Set form error instead of toast
            const errorMessage = t(
                'dashboard.events.room_booking_conflict_description',
                {
                    roomName: selectedRoom,
                }
            )
            setStartTimeError?.(errorMessage)
            return // Don't update the time
        }

        // Clear any existing error
        setStartTimeError?.(undefined)
        onStartTimeChange(time)
    }

    const handleDurationChange = (newDuration: number) => {
        // Check if the new duration would conflict with existing bookings
        if (startTime) {
            const currentStart = timeToMinutes(startTime)
            const proposedEnd = currentStart + newDuration

            const wouldConflict = disabledTimeRanges?.some((range) => {
                const rangeStart = timeToMinutes(range.start)
                const rangeEnd = timeToMinutes(range.end)
                return isTimeRangeOverlap(
                    currentStart,
                    proposedEnd,
                    rangeStart,
                    rangeEnd
                )
            })

            if (wouldConflict && selectedRoom) {
                // Set form error instead of toast
                setDurationError?.(
                    `⚠️ Thời lượng ${newDuration} phút sẽ bị trùng với lịch họp khác`
                )
                return // Don't update the duration
            }
        }

        // Clear any existing error
        setDurationError?.(undefined)
        onDurationChange(newDuration)
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className={`space-y-4 ${className}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Time Picker */}
                <div>
                    <TimePickerInput
                        value={startTime}
                        onChange={handleStartTimeChange}
                        placeholder={
                            startTimeLabel || t('dashboard.events.start_time')
                        }
                        disabledTimeRanges={disabledTimeRanges}
                    />
                    {startTimeError && (
                        <p className="text-red-500 text-sm mt-1">
                            {startTimeError}
                        </p>
                    )}
                </div>

                {/* Duration Picker */}
                <div>
                    <DurationPicker
                        currentTime={startTime}
                        disabledRanges={disabledTimeRanges}
                        step={1}
                        showDays
                        value={duration}
                        onChange={handleDurationChange}
                        placeholder={
                            durationLabel || t('dashboard.events.duration')
                        }
                    />
                    {durationError && (
                        <p className="text-red-500 text-sm mt-1">
                            {durationError}
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AvailableTimePicker
